import { Fragment, useEffect } from "react";
import type { ReactNode } from "react";
import { StyleSheet, Text, View } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";
import Svg, { Circle, Path } from "react-native-svg";
import { CHECKOUT_WORDS, goodsOfSession, money } from "@libs/checkout-core";
import type { BuyerSession, CheckoutGoods } from "@libs/checkout-core";
import LPressable from "@components/LPressable";
import { CONTROL_HEIGHT, RADIUS, colors } from "./colors";
import { EASE, RISE_MS, RISE_PX, RISE_STAGGER_MS } from "./motion";
import { OrderSummaryCard } from "./OrderSummaryCard";

/**
 * How a paid checkout ends, as the tickets sheet draws it: the tick and
 * "You're going" ("It's yours" for an order with no event, such as a music
 * unlock) — the words the Pay button just ended on, in the place it stood —
 * then, rising in one by one, where the tickets (or the receipt) went, the
 * order and what paid. Done goes back to the details.
 */
export function SuccessView({
  session,
  method,
  email,
  locale,
  onDone,
}: {
  session: BuyerSession;
  method: string | null;
  email: string | null;
  locale?: string;
  onDone: () => void;
}) {
  const paid = (session.total_minor ?? 0) > 0;
  return (
    <PurchaseSuccess
      goods={goodsOfSession(session)}
      email={email}
      order={<OrderSummaryCard session={session} locale={locale} />}
      paid={
        paid
          ? `Paid ${money(session.total_minor, session.currency, locale)}${method ? ` · ${method}` : ""}`
          : null
      }
      onDone={onDone}
    />
  );
}

/**
 * How every purchase in the sheet ends (PurchaseSheet): the tick and what
 * was sold's words ("You're going", "It's yours"), then where the tickets
 * or the receipt went, the order and what paid, and Done. The engine's
 * checkout ends on it (SuccessView); so does an order complete before any
 * checkout (a free unlock).
 */
export function PurchaseSuccess({
  goods,
  email,
  order,
  paid,
  onDone,
}: {
  goods: CheckoutGoods;
  email: string | null;
  /** The order's summary card. */
  order: ReactNode;
  /** "Paid £12.00 · Visa 4242"; null when nothing was paid. */
  paid: string | null;
  onDone: () => void;
}) {
  const rows: ReactNode[] = [
    email ? (
      <Text key="email" style={styles.muted}>
        {goods === "items" ? "Receipt" : "Tickets"} sent by email to {email}.
      </Text>
    ) : null,
    <Fragment key="order">{order}</Fragment>,
    paid ? (
      <Text key="paid" style={styles.muted}>
        {paid}
      </Text>
    ) : null,
    <LPressable
      key="done"
      accessibilityRole="button"
      onPress={onDone}
      sentryEventName="NativeCheckout.Done"
      style={styles.done}
    >
      <Text style={styles.doneText}>Done</Text>
    </LPressable>,
  ].filter(Boolean);
  return (
    <View accessibilityRole="summary" style={styles.screen}>
      {/* Where the status stood: taken over in place, not re-shown. */}
      <View style={styles.header}>
        <Svg width={28} height={28} viewBox="0 0 28 28">
          <Circle cx={14} cy={14} r={14} fill={colors.success} />
          <Path
            d="M8.2 14.4 12.1 18.2 19.8 10.4"
            fill="none"
            stroke="#fff"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.6}
          />
        </Svg>
        <Text accessibilityRole="header" style={styles.title}>
          {CHECKOUT_WORDS[goods].payStatus.done}
        </Text>
      </View>
      {rows.map((row, index) => (
        <Rise key={index} index={index}>
          {row}
        </Rise>
      ))}
    </View>
  );
}

/** One row rising in, after the title has had its moment. */
function Rise({ index, children }: { index: number; children: ReactNode }) {
  const shown = useSharedValue(0);
  useEffect(() => {
    shown.value = withDelay(
      60 + index * RISE_STAGGER_MS,
      withTiming(1, { duration: RISE_MS, easing: EASE.settle }),
    );
  }, [index, shown]);
  const style = useAnimatedStyle(() => ({
    opacity: shown.value,
    transform: [{ translateY: RISE_PX * (1 - shown.value) }],
  }));
  return <Animated.View style={style}>{children}</Animated.View>;
}

const styles = StyleSheet.create({
  screen: { gap: 12 },
  header: { alignItems: "center", flexDirection: "row", gap: 10 },
  title: {
    color: colors.text,
    fontSize: 26,
    fontWeight: "600",
    letterSpacing: -0.5,
  },
  muted: { color: colors.secondary, fontSize: 13 },
  done: {
    alignItems: "center",
    backgroundColor: colors.primary,
    borderRadius: RADIUS,
    justifyContent: "center",
    marginTop: 4,
    minHeight: CONTROL_HEIGHT,
  },
  doneText: { color: colors.onPrimary, fontSize: 15, fontWeight: "600" },
});
